import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { Grid, TextField, Button, MenuItem, Select, InputLabel, FormControl, FormHelperText } from '@mui/material';
import FormPage from '../components/FormPage';
import { createVehicle, fetchCustomers } from '../services/ApiService';

const MANUFACTURERS = ["Daewoo", "Mitsubishi", "FAW", "ISUZU", "Hino", "Renault", "Volvo", "DongFeng", "UD", "Toyota", "Other"];
const VEHICLE_TYPES = ["Trailer", "Buses"];

const FIELDS = [
  { label: "Registration No.", name: "registration_number", required: true, placeholder: "e.g. ABC-123" },
  { label: "Model Name", name: "model_name", placeholder: "e.g. Hino 500" },
  { label: "Model Year", name: "model_year", placeholder: "e.g. 2022" },
  { label: "Chassis No.", name: "chassis_number", placeholder: "Vehicle chassis number" },
  { label: "Engine No.", name: "engine_number", placeholder: "Vehicle engine number" },
  { label: "Color", name: "color", placeholder: "e.g. White" },
];

export function AddVehicleForm() {
  const navigate = useNavigate();
  const [haulierOptions, setHaulierOptions] = React.useState({});
  const [formData, setFormData] = React.useState({
    vehicle_type: "", model_name: "", model_year: "", color: "", chassis_number: "",
    registration_number: "", manufacturer: "", engine_number: "", customer: "", remarks: "",
  });
  const [formErrors, setFormErrors] = React.useState({});

  React.useEffect(() => {
    fetchCustomers().then((res) => {
      setHaulierOptions(res.data.reduce((acc, c) => ({ ...acc, [c.id]: c.name }), {}));
    }).catch(() => {});
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleCancel = () => navigate('/add-truck');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormErrors({});
    try {
      await createVehicle(formData);
      navigate('/add-truck', { state: { successMessage: 'Vehicle added successfully' } });
    } catch (err) {
      const errorData = err.response?.data;
      if (errorData?.errors) setFormErrors(errorData.errors);
      else setFormErrors({ general: 'Error while adding vehicle' });
    }
  };

  return (
    <FormPage
      title="Add New Vehicle"
      backTo="/add-truck"
      onSubmit={handleSubmit}
      actions={<>
        <Button variant="outlined" onClick={handleCancel}>Cancel</Button>
        <Button variant="contained" type="submit">Save</Button>
      </>}
    >
      <Grid container spacing={2}>
        {FIELDS.map((field) => (
          <Grid item xs={12} sm={6} key={field.name}>
            <TextField
              fullWidth label={field.label} name={field.name} placeholder={field.placeholder}
              value={formData[field.name]} onChange={handleChange}
              required={field.required || false} error={!!formErrors[field.name]} helperText={formErrors[field.name]}
            />
          </Grid>
        ))}

        <Grid item xs={12} sm={6}>
          <TextField select fullWidth label="Manufacturer" name="manufacturer" value={formData.manufacturer} onChange={handleChange}
            error={!!formErrors.manufacturer} helperText={formErrors.manufacturer} SelectProps={{ displayEmpty: true }}>
            <MenuItem value="" disabled>Select manufacturer</MenuItem>
            {MANUFACTURERS.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField select fullWidth label="Vehicle Type" name="vehicle_type" value={formData.vehicle_type} onChange={handleChange}
            error={!!formErrors.vehicle_type} helperText={formErrors.vehicle_type} SelectProps={{ displayEmpty: true }}>
            <MenuItem value="" disabled>Select vehicle type</MenuItem>
            {VEHICLE_TYPES.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
          </TextField>
        </Grid>

        <Grid item xs={12}>
          <TextField fullWidth label="Remarks" name="remarks" placeholder="Any additional notes" value={formData.remarks} onChange={handleChange}
            error={!!formErrors.remarks} helperText={formErrors.remarks} />
        </Grid>

        <Grid item xs={12}>
          <FormControl fullWidth error={!!formErrors.customer}>
            <InputLabel id="haulier-add-label">Customer</InputLabel>
            <Select labelId="haulier-add-label" label="Customer" name="customer" value={formData.customer} onChange={handleChange} displayEmpty>
              <MenuItem value="" disabled>Select a customer</MenuItem>
              {Object.entries(haulierOptions).map(([key, value]) => <MenuItem key={key} value={key}>{value}</MenuItem>)}
            </Select>
            {formErrors.customer && <FormHelperText>{formErrors.customer}</FormHelperText>}
          </FormControl>
        </Grid>

        {formErrors.general && (
          <Grid item xs={12}>
            <FormHelperText error>{formErrors.general}</FormHelperText>
          </Grid>
        )}
      </Grid>
    </FormPage>
  );
}
